import { cardLabel, type Card } from '@uno/shared';
import { useState } from 'react';
import { useT } from '../hooks';
import type { CardTheme } from '../theme/types';
import { backUrl, faceUrl } from '../theme/types';

const GLYPH: Record<string, string> = {
  skip: '⊘', // circle with slash
  reverse: '⇄', // left-right arrows
  draw2: '+2',
  wild: '✦',
  wild4: '+4',
};

interface Props {
  card?: Card;
  theme: CardTheme;
  faceDown?: boolean;
  /** Highlights the card as legal to play. */
  playable?: boolean;
  /** Dims a card that cannot be played right now. */
  dimmed?: boolean;
  selected?: boolean;
  /**
   * Render as a button even with no handler.
   *
   * A card that alternates between `<div>` and `<button>` as it becomes
   * playable is a *new DOM node* each time, so its deal-in animation replays
   * and the card appears to blink. Hand cards pin the element type instead.
   */
  interactive?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

/**
 * Renders one card.
 *
 * PNG themes are attempted first; any missing or broken image silently falls
 * back to the CSS rendering, so a half-finished theme folder still plays fine.
 */
export function CardFace({
  card,
  theme,
  faceDown = false,
  playable = false,
  dimmed = false,
  selected = false,
  interactive = false,
  onClick,
  className = '',
  style,
  ariaLabel,
}: Props) {
  const t = useT();
  const [imgFailed, setImgFailed] = useState(false);

  const classes = [
    'card',
    faceDown ? 'card--back' : `card--${card?.color ?? 'wild'}`,
    playable ? 'card--playable' : '',
    dimmed ? 'card--dimmed' : '',
    selected ? 'card--selected' : '',
    onClick ? 'card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Colour words stay in English: they name the card face, and every theme's
  // art uses the same four colours regardless of the player's language.
  const label = faceDown
    ? t.game.faceDownCard
    : card
      ? `${card.color === 'wild' ? '' : `${card.color} `}${cardLabel(card)}`
      : t.game.faceDownCard;

  const src = faceDown ? backUrl(theme) : card ? faceUrl(theme, card) : null;
  const usePng = theme.kind === 'png' && src && !imgFailed;

  const body = usePng ? (
    <img
      className="card__png"
      src={src}
      alt={label}
      draggable={false}
      onError={() => setImgFailed(true)}
    />
  ) : faceDown ? (
    <span className="card__back-mark" aria-hidden="true">
      UNO
    </span>
  ) : card ? (
    <>
      <span className="card__corner card__corner--tl" aria-hidden="true">
        {GLYPH[card.kind] ?? card.value}
      </span>
      <span className="card__oval" aria-hidden="true" />
      <span className={`card__center ${card.kind === 'wild' ? 'card__center--wild' : ''}`} aria-hidden="true">
        {card.kind === 'wild' ? <WildPie /> : (GLYPH[card.kind] ?? card.value)}
      </span>
      <span className="card__corner card__corner--br" aria-hidden="true">
        {GLYPH[card.kind] ?? card.value}
      </span>
    </>
  ) : null;

  const commonProps = {
    'data-card-id': card?.id,
    className: classes,
    style: { aspectRatio: `${theme.width} / ${theme.height}`, ...style },
    title: ariaLabel ?? label,
  };

  if (onClick || interactive) {
    return (
      <button
        type="button"
        {...commonProps}
        disabled={!onClick}
        onClick={onClick}
        aria-label={ariaLabel ?? label}
      >
        {body}
      </button>
    );
  }
  return (
    <div {...commonProps} role="img" aria-label={ariaLabel ?? label}>
      {body}
    </div>
  );
}

/** The four-colour wheel on a Wild card. */
function WildPie() {
  return (
    <svg viewBox="0 0 100 100" className="card__pie" aria-hidden="true">
      <path d="M50 50 L50 0 A50 50 0 0 1 100 50 Z" fill="var(--c-red)" />
      <path d="M50 50 L100 50 A50 50 0 0 1 50 100 Z" fill="var(--c-yellow)" />
      <path d="M50 50 L50 100 A50 50 0 0 1 0 50 Z" fill="var(--c-green)" />
      <path d="M50 50 L0 50 A50 50 0 0 1 50 0 Z" fill="var(--c-blue)" />
    </svg>
  );
}
